import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RequestError, NotificationsApi } from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { UIService } from '@services';
import { AuthStore, NotificationsStore } from '@store';

import { NotificationsActions } from './notifications.actions';

@Injectable()
export class NotificationsEffects {
  // SUBSCRIPTIONS

  getSubscription$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.getMailSubscription),
      switchMap(() =>
        this.notificationsApi.getMailSubscription().pipe(
          map((response) =>
            NotificationsActions.getMailSubscriptionSuccess({
              notifications: response.data.isActive,
              email: response.data.email,
            })
          ),
          catchError(() => of(NotificationsActions.getMailSubscriptionError()))
        )
      )
    );
  });

  setSubscription$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.setMailSubscription),
      switchMap((action) => {
        this.uiService.blockUI();

        return this.notificationsApi.updateMailSubscription(action.notifications).pipe(
          map(() => {
            this.uiService.unblockUI();
            this.toast.info({
              message: this.translate.instant('+SETTINGS.NOTIFICATIONS.CHANGE_SUCCESS'),
            });

            return NotificationsActions.setMailSubscriptionSuccess({
              notifications: action.notifications,
            });
          }),
          catchError(() => {
            this.uiService.unblockUI();
            this.toast.error({
              message: this.translate.instant('+SETTINGS.NOTIFICATIONS.CHANGE_FAILURE'),
            });

            return of(NotificationsActions.setMailSubscriptionError());
          })
        );
      })
    );
  });

  // NOTIFICATION RECIPIENTS

  getNotificationRecipients$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.getNotificationRecipients),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([_action, companyId]) =>
        this.notificationsApi.getNotificationRecipients(companyId).pipe(
          map((response) => {
            if (!!response.cursor.next) {
              return NotificationsActions.getNotificationRecipientsMore({
                cursor: response.cursor.next,
                data: response.data,
              });
            } else {
              return NotificationsActions.getNotificationRecipientsSuccess({
                data: response.data,
              });
            }
          }),
          catchError((error: RequestError) => {
            return of(NotificationsActions.getNotificationRecipientsError({ error }));
          })
        )
      )
    );
  });

  getNotificationRecipientsMore$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.getNotificationRecipientsMore),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([action, companyId]) =>
        this.notificationsApi.getNotificationRecipients(companyId, action.cursor).pipe(
          map((response) => {
            if (!!response.cursor.next) {
              return NotificationsActions.getNotificationRecipientsMore({
                cursor: response.cursor.next,
                data: [].concat(action.data, response.data),
              });
            } else {
              return NotificationsActions.getNotificationRecipientsSuccess({
                data: [].concat(action.data, response.data),
              });
            }
          }),
          catchError((error: RequestError) => {
            return of(NotificationsActions.getNotificationRecipientsError({ error }));
          })
        )
      )
    );
  });

  updateNotificationRecipient$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.updateNotificationRecipient),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([action, companyId]) => {
        const isActiveValue = action.recipient.isActive;

        return this.notificationsApi
          .updateNotificationRecipient(companyId, action.isActive, action.recipient)
          .pipe(
            map(() => NotificationsActions.updateNotificationRecipientSuccess()),
            catchError((error: any) => {
              return of(
                NotificationsActions.updateNotificationRecipientError({
                  recipientToRevert: action.recipient,
                  isActiveValue,
                  error,
                })
              );
            })
          );
      })
    );
  });

  // CENTRAL EMAIL

  getCentralEmail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.getCentralEmail),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([_action, companyId]) =>
        this.notificationsApi.getCentralEmail(companyId).pipe(
          map((response) => {
            return NotificationsActions.getCentralEmailSuccess({
              data: response,
            });
          }),
          catchError((error: RequestError) => {
            const isNotFound = error.statusCode === 404;

            if (isNotFound) {
              return of(NotificationsActions.getCentralEmailNoEmailFound());
            } else {
              return of(NotificationsActions.getCentralEmailError({ error }));
            }
          })
        )
      )
    );
  });

  setCentralEmail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.setCentralEmail),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([action, companyId]) =>
        this.notificationsApi.setCentralEmail(companyId, action.email).pipe(
          map((response) => {
            this.notificationsStore.getNotificationRecipients();

            return NotificationsActions.setCentralEmailSuccess({
              data: response,
            });
          }),
          catchError((error) => of(NotificationsActions.setCentralEmailError({ error })))
        )
      )
    );
  });

  updateCentralEmail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.updateCentralEmail),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([action, companyId]) =>
        this.notificationsApi.updateCentralEmail(companyId, action.email).pipe(
          map((response) => {
            this.notificationsStore.getNotificationRecipients();

            return NotificationsActions.updateCentralEmailSuccess({
              data: response,
            });
          }),
          catchError((error) => of(NotificationsActions.updateCentralEmailError({ error })))
        )
      )
    );
  });

  deleteCentralEmail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationsActions.deleteCentralEmail),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      switchMap(([_action, companyId]) =>
        this.notificationsApi.deleteCentralEmail(companyId).pipe(
          map(() => {
            this.notificationsStore.getNotificationRecipients();

            return NotificationsActions.deleteCentralEmailSuccess();
          }),
          catchError((error) => of(NotificationsActions.deleteCentralEmailError({ error })))
        )
      )
    );
  });

  constructor(
    private notificationsApi: NotificationsApi,
    private notificationsStore: NotificationsStore,
    private authStore: AuthStore,
    private actions$: Actions,
    private toast: ViToastService,
    private uiService: UIService,
    private translate: TranslateService
  ) {}
}
